import React, { useState, useEffect } from "react";
import client, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useFetch, PageHeader, Loading, FileUpload, fileSrc } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Save, Image, Palette } from "lucide-react";

export default function Settings() {
  const { data, loading } = useFetch("/settings", []);
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  if (loading || !f) return <Loading />;

  const ch = (k, v) => setF({ ...f, [k]: v });
  const save = async () => { try { await client.put("/settings", f); toast.success("Settings saved"); } catch (e) { toast.error(apiError(e)); } };

  const G = ({ label, k, type = "text" }) => (
    <div><Label className="mb-1.5 block">{label}</Label><Input type={type} value={f[k] ?? ""} onChange={(e) => ch(k, type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`setting-${k}`} /></div>
  );

  const ImageField = ({ label, k, testid }) => (
    <div>
      <Label className="mb-1.5 block">{label}</Label>
      <div className="flex items-center gap-3">
        <div className="h-16 w-16 rounded-lg border border-border bg-muted flex items-center justify-center overflow-hidden shrink-0">
          {f[k] ? <img src={f[k].startsWith("/api") ? fileSrc(f[k]) : f[k]} alt="" className="h-full w-full object-contain" /> : <Image size={20} className="text-muted-foreground" />}
        </div>
        <div className="flex flex-col gap-1.5 flex-1 min-w-0">
          <FileUpload accept="image/*" label="Upload" testid={testid} onUploaded={(file) => ch(k, file.url)} />
          {f[k] && <button type="button" onClick={() => ch(k, "")} className="text-xs text-rose-500 text-start">Remove</button>}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure your academy" testid="settings-page">
        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} data-testid="settings-save"><Save size={16} className="me-1" /> Save</Button>
      </PageHeader>
      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding"><Palette size={14} className="me-1" /> Branding</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-bold font-heading mb-4">Academy Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <G label="Academy Name" k="academy_name" />
                <G label="Contact Phone" k="contact_phone" />
                <G label="Contact Email" k="contact_email" />
                <G label="City" k="city" />
                <G label="Address" k="address" />
                <G label="Parent Email Domain" k="parent_email_domain" />
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="font-bold font-heading mb-4">Pricing & Season</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <G label="Registration Fee (MAD)" k="registration_fee" type="number" />
                <G label="Monthly Fee (MAD)" k="monthly_fee" type="number" />
                <G label="Season Year" k="season_year" type="number" />
                <G label="Currency" k="currency" />
              </div>
              <p className="text-xs text-muted-foreground mt-4">Age category is auto-calculated as (Season Year − Birth Year), clamped between U8 and U15.</p>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="branding" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-bold font-heading mb-4">Logos & Images</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <ImageField label="Academy Logo" k="logo_url" testid="upload-logo" />
                <ImageField label="Favicon" k="favicon_url" testid="upload-favicon" />
                <ImageField label="Login Background" k="login_bg_url" testid="upload-login-bg" />
                <ImageField label="Parent Portal Background" k="portal_bg_url" testid="upload-portal-bg" />
                <ImageField label="General Background" k="background_url" testid="upload-bg" />
              </div>
            </Card>
            <Card className="p-6">
              <h3 className="font-bold font-heading mb-4">Colors</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="mb-1.5 block">Primary Color</Label><div className="flex gap-2"><input type="color" value={f.primary_color || "#059669"} onChange={(e) => ch("primary_color", e.target.value)} className="h-10 w-14 rounded border border-border" data-testid="color-primary" /><Input value={f.primary_color || ""} onChange={(e) => ch("primary_color", e.target.value)} /></div></div>
                <div><Label className="mb-1.5 block">Secondary Color</Label><div className="flex gap-2"><input type="color" value={f.secondary_color || "#0F172A"} onChange={(e) => ch("secondary_color", e.target.value)} className="h-10 w-14 rounded border border-border" data-testid="color-secondary" /><Input value={f.secondary_color || ""} onChange={(e) => ch("secondary_color", e.target.value)} /></div></div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">Uploaded images are stored securely and applied to the login page, parent portal and sidebar automatically.</p>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
